import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  AdminRequest,
  Bid,
  RequestEvent,
  RequestPatch,
  RequestStatus,
  claimRequest,
  dismissRequest,
  getAdminRequest,
  patchAdminRequest,
  releaseRequest,
} from '../../api/marketplace';
import RequestFields, { RequestFieldValues } from './RequestFields';
import {
  BID_STATUS_LABELS,
  formatDateTime,
  formatPrice,
  parseCodes,
  statusLabel,
} from './marketplaceFormat';
import './Marketplace.css';

// Statuses in which the backend accepts PATCH and each admin transition
// (contract section 2 and 4).
const EDITABLE: RequestStatus[] = ['DETECTED', 'RESERVED', 'RELEASED'];
export const ACTIONS_BY_STATUS = {
  claim: ['DETECTED', 'RESERVED'] as RequestStatus[],
  release: ['DETECTED', 'RESERVED'] as RequestStatus[],
  dismiss: ['DETECTED'] as RequestStatus[],
};

const toValues = (request: AdminRequest): RequestFieldValues => ({
  serviceType: request.serviceType,
  countries: (request.countries || []).join(', '),
  language: request.language || '',
  urgency: request.urgency || 'normal',
  anonymisedSummary: request.anonymisedSummary || '',
  userId: request.userId || '',
  customerEmail: request.customerEmail || '',
  customerName: request.customerName || '',
});

// Only send what changed. Empty customer fields become null.
const diff = (request: AdminRequest, values: RequestFieldValues): RequestPatch => {
  const patch: RequestPatch = {};
  const countries = parseCodes(values.countries, true);
  if (values.serviceType !== request.serviceType) patch.serviceType = values.serviceType;
  if (countries.join(',') !== (request.countries || []).join(',')) patch.countries = countries;
  const language = values.language.trim().toLowerCase();
  if (language !== (request.language || '')) patch.language = language;
  if (values.urgency !== request.urgency) patch.urgency = values.urgency;
  const summary = values.anonymisedSummary.trim();
  if (summary !== (request.anonymisedSummary || '')) patch.anonymisedSummary = summary;
  // Customer fields come from the support ticket and are read-only here.
  return patch;
};

const AdminRequestDetailPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const { currentUser } = useAuth();
  const [request, setRequest] = useState<AdminRequest | null>(null);
  const [bids, setBids] = useState<Bid[]>([]);
  const [events, setEvents] = useState<RequestEvent[]>([]);
  const [values, setValues] = useState<RequestFieldValues | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dismissing, setDismissing] = useState(false);
  const [dismissReason, setDismissReason] = useState('');

  const load = useCallback(async () => {
    if (!currentUser || !requestId) return;
    try {
      const token = await currentUser.getIdToken();
      const data = await getAdminRequest(token, requestId);
      setRequest(data.request);
      setBids(data.bids);
      setEvents(data.events);
      setValues(data.request ? toValues(data.request) : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the request.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (label: string, fn: (token: string) => Promise<unknown>) => {
    if (!currentUser) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn(await currentUser.getIdToken());
      setNotice(label);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p>Loading request...</p>;
  if (!request || !values) {
    return (
      <div className="marketplace-page">
        <Link to="/marketplace/admin/requests" className="service-link">Back to requests</Link>
        <div className="status-error-banner" role="alert">{error || 'Request not found.'}</div>
      </div>
    );
  }

  const status = request.status;
  const editable = EDITABLE.includes(status);
  const patch = diff(request, values);
  const dirty = Object.keys(patch).length > 0;
  const can = (action: keyof typeof ACTIONS_BY_STATUS) => ACTIONS_BY_STATUS[action].includes(status);
  const ticketId = request.sourceRef?.ticketId;
  const confidence = request.classifier?.confidence;

  const save = () => run('Saved.', token => patchAdminRequest(token, request.id, patch));
  const claim = () => {
    if (!window.confirm('Claim this request for Thais? It will not go to partner lawyers.')) return;
    run('Claimed by Thais.', token => claimRequest(token, request.id));
  };
  const release = () => {
    if (!window.confirm('Release this request to partner lawyers? The customer is asked for consent first.')) return;
    run('Released to partner lawyers.', token => releaseRequest(token, request.id));
  };
  const dismiss = async () => {
    if (!dismissReason.trim()) {
      setError('Give a reason for dismissing.');
      return;
    }
    await run('Dismissed.', token => dismissRequest(token, request.id, dismissReason.trim()));
    setDismissing(false);
    setDismissReason('');
  };

  return (
    <div className="marketplace-page">
      <Link to="/marketplace/admin/requests" className="service-link">Back to requests</Link>
      <div className="task-view-header">
        <h2>Marketplace request</h2>
        <span className={`marketplace-status marketplace-status--${status}`}>{statusLabel(status)}</span>
      </div>
      {error && <div className="status-error-banner" role="alert">{error}</div>}
      {notice && <div className="marketplace-notice" role="status">{notice}</div>}

      <section className="marketplace-panel">
        <dl className="marketplace-facts">
          <div><dt>Requested</dt><dd>{formatDateTime(request.requestedAt || request.createdAt)}</dd></div>
          <div><dt>Source</dt><dd>{request.source}</dd></div>
          <div>
            <dt>Support ticket</dt>
            <dd>
              {ticketId ? (
                <Link to={`/support/ticket/${ticketId}`} className="service-link">Open ticket</Link>
              ) : 'n/a'}
            </dd>
          </div>
          {request.reservedUntil && (
            <div><dt>Reserved until</dt><dd>{formatDateTime(request.reservedUntil)}</dd></div>
          )}
          {request.bidDeadline && (
            <div><dt>Bid deadline</dt><dd>{formatDateTime(request.bidDeadline)}</dd></div>
          )}
          {request.consentAt && <div><dt>Consent</dt><dd>{formatDateTime(request.consentAt)}</dd></div>}
          {request.dismissReason && <div><dt>Dismiss reason</dt><dd>{request.dismissReason}</dd></div>}
          {request.cancelReason && <div><dt>Cancel reason</dt><dd>{request.cancelReason}</dd></div>}
        </dl>
        {request.classifier && (
          <div className="marketplace-classifier" aria-label="Classifier">
            <strong>Classifier</strong>
            {typeof confidence === 'number' && <span> {Math.round(confidence * 100)}% confidence</span>}
            {request.classifier.model && <span className="marketplace-muted"> ({request.classifier.model})</span>}
            {request.classifier.reason && <p>{request.classifier.reason}</p>}
          </div>
        )}
      </section>

      <section className="marketplace-panel">
        <h3>Details</h3>
        {!editable && <p className="marketplace-hint">Details can only be edited while detected, reserved or released.</p>}
        <RequestFields values={values} onChange={setValues} disabled={!editable || busy} showCustomer={false} />
        <dl className="marketplace-facts" aria-label="Customer">
          <div><dt>Customer name</dt><dd>{request.customerName || 'n/a'}</dd></div>
          <div><dt>Customer email</dt><dd>{request.customerEmail || 'n/a'}</dd></div>
          <div><dt>User ID</dt><dd>{request.userId || 'n/a'}</dd></div>
        </dl>
        {editable && (
          <div className="marketplace-actions">
            <button type="button" className="download-button" onClick={save} disabled={busy || !dirty}>
              Save changes
            </button>
            {dirty && (
              <button type="button" className="logout-button" onClick={() => setValues(toValues(request))} disabled={busy}>
                Discard
              </button>
            )}
          </div>
        )}
      </section>

      <section className="marketplace-panel" aria-label="Actions">
        <h3>Actions</h3>
        {dirty && <p className="marketplace-hint">Save your changes before changing the status.</p>}
        <div className="marketplace-actions">
          <button type="button" className="download-button" onClick={claim} disabled={!can('claim') || busy || dirty}>
            Claim (Thais)
          </button>
          <button
            type="button"
            className="download-button"
            onClick={release}
            disabled={!can('release') || !request.userId || busy || dirty}
            title={!request.userId ? 'Set a user ID before releasing.' : undefined}
          >
            Release to partner lawyers
          </button>
          <button
            type="button"
            className="logout-button"
            onClick={() => setDismissing(true)}
            disabled={!can('dismiss') || busy}
          >
            Dismiss
          </button>
        </div>
        {can('release') && !request.userId && (
          <p className="marketplace-hint">
            Release needs a Coepi account: this customer wrote from an address with no account, so they cannot consent in the app yet.
          </p>
        )}
        <ul className="marketplace-hint marketplace-action-help">
          <li><strong>Claim (Thais)</strong>: Thais does it in-house. It never goes to partner lawyers; continue in the support ticket.</li>
          <li><strong>Release to partner lawyers</strong>: the customer is asked to consent first, then matching partner firms can bid.</li>
          <li><strong>Dismiss</strong>: not a marketplace request (classifier mistake or out of scope). Needs a reason.</li>
        </ul>
        {dismissing && can('dismiss') && (
          <div className="marketplace-dismiss">
            <label>
              Reason for dismissing
              <input
                type="text"
                value={dismissReason}
                onChange={e => setDismissReason(e.target.value)}
                placeholder="Not a marketplace request"
                autoFocus
              />
            </label>
            <div className="marketplace-actions">
              <button type="button" className="download-button" onClick={dismiss} disabled={busy}>
                Dismiss request
              </button>
              <button type="button" className="logout-button" onClick={() => setDismissing(false)} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="marketplace-panel">
        <h3>Bids ({bids.length})</h3>
        {bids.length === 0 ? (
          <p className="marketplace-hint">No bids yet.</p>
        ) : (
          <table className="marketplace-table">
            <thead>
              <tr>
                <th>Firm</th>
                <th>Price</th>
                <th>ETA</th>
                <th>Status</th>
                <th>Message</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {bids.map(bid => (
                <tr key={bid.firmId}>
                  <td>{bid.firmName}</td>
                  <td>{formatPrice(bid.priceCents, bid.priceType)}</td>
                  <td>{bid.etaDays} days</td>
                  <td>{BID_STATUS_LABELS[bid.status] || bid.status}</td>
                  <td className="marketplace-summary-cell" title={bid.message}>{bid.message}</td>
                  <td>{formatDateTime(bid.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="marketplace-panel">
        <h3>Timeline</h3>
        {events.length === 0 ? (
          <p className="marketplace-hint">No events.</p>
        ) : (
          <ol className="marketplace-timeline">
            {events.map((event, index) => (
              <li key={event.id || index}>
                <span className="marketplace-muted">{formatDateTime(event.at)}</span>{' '}
                <strong>
                  {event.from ? `${statusLabel(event.from)} to ${statusLabel(event.to)}` : statusLabel(event.to)}
                </strong>{' '}
                <span className="marketplace-muted">by {event.actorRole}</span>
                {event.note && <div>{event.note}</div>}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
};

export default AdminRequestDetailPage;
